import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Phone, Edit, Save, X, Camera, RotateCcw, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import apiClient from "../common/apiClient";
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import bowser from "bowser";
import { useUserProfile } from "../contexts/UserProfileContext";

const StaffManagement = () => {
  const { userProfile, loadingProfile, profileError } = useUserProfile();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const userId = Cookies.get("userId");
  const userHierarchy = Cookies.get("userHierarchy");

  useEffect(() => {
    if (userHierarchy === "subuser") {
      navigate("/dashboard");
      toast.error("Access Denied: Subusers cannot access staff management.");
    }
  }, [userHierarchy, navigate]);
  
  const [subUser, setSubUser] = useState([]);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState({});
  const [editingSubUser, setEditingSubUser] = useState({});
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [subUserLimit, setSubUserLimit] = useState(3);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subUserToDelete, setSubUserToDelete] = useState(null);
  const [deletedSubUsers, setDeletedSubUsers] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [recoveringId, setRecoveringId] = useState(null);
  const [profileData, setProfileData] = useState({
    companyName: "",
    address: "",
    isPremium: "",
  });

  const debounceTimers = useRef({});

  useEffect(() => {
    if (userProfile && userProfile.user) { // Add check for userProfile.user
      const data = userProfile.user;
      const subusers = userProfile.subusers;
      const subUserLimit = userProfile.user.subUserLimit;


      setSubUserLimit(subUserLimit ?? 3);

      const mappedSubUsers = subusers.map((sData) => ({
        id: sData._id,
        companyName: sData.companyName,
        name: sData.name,
        number: sData.number,
        email: sData.email,
        address: sData.address,
        profileImage: sData.profileImage || "",
        isActive: true
      }));

      setProfileData({
        companyName: data.companyName,
        address: data.address,
        isPremium: data.isPremium,
      });

      setSubUser(mappedSubUsers);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!userId || userHierarchy === "subuser") return;

    const fetchDeletedSubUsers = async () => {
      setLoadingDeleted(true);
      try {
        const response = await apiClient(
          "post",
          "/cjidnvij/ceksfbuebijn/user/qujaidaxc/v2/deletedSubusers/uijdisdna",
          { userId }
        );

        if (response.success && response.data?.success) {
          setDeletedSubUsers(response.data.data || []);
        }
      } finally {
        setLoadingDeleted(false);
      }
    };

    fetchDeletedSubUsers();
  }, [userId, userHierarchy]);

  const handleRecoverSubUser = async (staffUserId) => {
    setRecoveringId(staffUserId);
    try {
      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/qujaidaxc/v2/recoverSubuser/uijdisdna",
        { userId: staffUserId, status: "Recover" }
      );

      if (!response.success) {
        toast.error(response.error?.message || "Recover failed.");
        return;
      }

      if (response.data.success) {
        window.location.reload();
      } else {
        toast.error(response.data.message || "Recover failed.");
      }
    } catch (err) {
      toast.error("Unexpected failure. Please try again.");
    } finally {
      setRecoveringId(null);
    }
  };

  const handleSubUserBoilerPlate = () => {
    const maxSubUserLimit = subUserLimit;
    
     if (maxSubUserLimit === 0) {
    toast.error("You can't add staff.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
    });
    return false;
  }
    if (subUser.length >= maxSubUserLimit) {
      toast.info(`You can only add upto ${maxSubUserLimit} staff members.`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });
      return false;
    }

    const subUserFields = {
      id: subUser.length + 1,
      companyName: profileData.companyName,
      name: "",
      number: "",
      email: "",
      address: profileData.address,
      isPremium: profileData.isPremium,
      limit: 0,
      wrongPassLimit: 10,
      createdOn: new Date(),
      isActive: false
    };

    const checkInActive = subUser.filter((s) => s.isActive === false);
    if (checkInActive.length > 0) return false;

    setSubUser([...subUser, subUserFields]);
    setShowAddStaffModal(true);
  };

  const getDeviceDetails = () => {
    const browser = bowser.getParser(window.navigator.userAgent);
    const info = browser.getResult();
    return {
      browser: info.browser.name,
      browserVersion: info.browser.version,
      os: info.os.name,
      osVersion: info.os.version,
      platform: info.platform.type,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      userAgent: navigator.userAgent,
    };
  };

  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  };

  const getIPAddress = async () => {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip;
    } catch (error) {
      return null;
    }
  };

  const handleSubUserInputChange = useCallback((index, fieldName, e) => {
    const value = e.target.value;
    if (debounceTimers.current[`${index}-${fieldName}`]) {
      clearTimeout(debounceTimers.current[`${index}-${fieldName}`]);
    }
    debounceTimers.current[`${index}-${fieldName}`] = setTimeout(() => {
      setSubUser(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [fieldName]: value };
        return updated;
      });
    }, 300);
  }, []);

  const handleSubUserSaveProfile = async (index) => {
    const user = subUser[index];
    const { name, companyName, number, email, address, isPremium } = user;
    const userHierarchy = 'subuser';
    const parentUserId = userId;

    setError(prev => {
      const newError = { ...prev };
      delete newError[index];
      return newError;
    });

    if (!name || !number || !email) {
      setError(prev => ({ ...prev, [index]: "All fields are required." }));
      return;
    }

    const contactPattern = /^[0-9]{10}$/;
    if (!contactPattern.test(number)) {
      setError(prev => ({ ...prev, [index]: "Invalid contact number format." }));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(prev => ({ ...prev, [index]: "Please enter a valid email address." }));
      return;
    }

    setLoading(prev => ({ ...prev, [index]: true }));

    try {
      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/v2/signin/vkjdbfuhe/nkdkjbed",
        { companyName, email, address, name, number, userHierarchy, parentUserId, isPremium }
      );

      if (!response.success) {
        setError(prev => ({ ...prev, [index]: response.error?.message || "Add staff create failed." }));
        return;
      }

      if (response.data.status === "success") {
        window.location.reload();
      } else {
        setError(prev => ({ ...prev, [index]: response.data.message || "Unexpected error" }));
      }
    } catch (err) {
      setError(prev => ({ ...prev, [index]: "Unexpected failure. Please try again." }));
    } finally {
      setLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleSubUserUpdateProfile = async (data, index) => {
    const token = Cookies.get("token");
    if (!token) {
      toast.error("No authentication token found");
      return;
    }

    const { name, id: staffUserId } = data;

    setError(prev => {
      const newError = { ...prev };
      delete newError[index];
      return newError;
    });

    if (!name) {
      setError(prev => ({ ...prev, [index]: "Please enter the name." }));
      return;
    }

    setLoading(prev => ({ ...prev, [index]: true }));

    try {
      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/v2/editprofile",
        { name, userId: staffUserId },
      );

      if (!response.success) {
        setError(prev => ({ ...prev, [index]: response.error?.message || "Update failed." }));
        return;
      }

      if (response.data.success) {
        window.location.reload();
      } else {
        setError(prev => ({ ...prev, [index]: response.data.message || "Unexpected error" }));
      }
    } catch (err) {
      setError(prev => ({ ...prev, [index]: "Unexpected failure. Please try again." }));
    } finally {
      setLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleRemove = (data, index) => {
    setSubUserToDelete({ data, index });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (subUserToDelete) {
      const { data, index } = subUserToDelete;
      await handleDeleteSubUser(data, index);
      setShowDeleteConfirm(false);
      setSubUserToDelete(null);
    }
  };

  const handleDeleteSubUser = async (data, index) => {
    if (!data.isActive) {
      setSubUser(prev => prev.filter(user => user.id !== data.id));
      setShowAddStaffModal(false);
      setError(prev => {
        const newError = { ...prev };
        delete newError[index];
        return newError;
      });
      return;
    }

    const staffUserId = data.id;
    try {
      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/qujaidaxc/v2/deleteSubuser/uijdisdna",
        { userId: staffUserId }
      );

      if (!response.success) {
        toast.error(response.error?.message || "Delete failed.");
        return;
      }

      if (response.data.success) {
        window.location.reload();
      }
    } catch (err) {
      toast.error("Unexpected failure. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("Staff Management", "સ્ટાફ મેનેજમેન્ટ")}</h1>
        <Button 
          onClick={handleSubUserBoilerPlate}
          className="gradient-primary border-0 text-primary-foreground gap-1.5 shadow-md"
        >
          <Plus size={16} /> {t("Add Staff", "સ્ટાફ ઉમેરો")}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {subUser.filter(s => s.isActive).map((sData, sIndex) => {
          const originalIndex = subUser.findIndex(u => u.id === sData.id);
          const isEditing = editingSubUser[originalIndex];
          const bgColors = ['#ede9fe', '#d1fae5', '#fef3c7', '#ffe4e6'];
          const avatarBg = bgColors[originalIndex % bgColors.length];
          const textColors = ['#6200e8', '#10b981', '#f59e0b', '#e11d48'];
          const badgeColor = textColors[originalIndex % textColors.length];
          const imageUrl = sData.profileImage ? `${sData.profileImage}${sData.profileImage.includes('?') ? '&' : '?'}_=${Date.now()}` : "";

          if (isEditing) {
            return (
              <div key={sData.id} className="col-span-full border border-gray-200 rounded-xl p-6 bg-gray-50 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">{t("Edit Staff Member", "સ્ટાફ મેમ્બર એડિટ કરો")}: {sData.name}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingSubUser(prev => ({ ...prev, [originalIndex]: false }))}
                      className="gap-1"
                    >
                      <X size={14} /> {t("Cancel", "રદ કરો")}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemove(sData, originalIndex)}
                      className="gap-1"
                    >
                      <Trash2 size={14} /> {t("Remove", "દૂર કરો")}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">{t("Name", "નામ")}</label>
                    <input
                      type="text"
                      onChange={(e) => handleSubUserInputChange(originalIndex, 'name', e)}
                      defaultValue={sData.name}
                      placeholder={t("Enter name", "નામ દાખલ કરો")}
                      className="w-full border border-gray-300 rounded-lg p-2.5 bg-white outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">{t("Contact Number", "સંપર્ક નંબર")}</label>
                    <input
                      type="text"
                      readOnly
                      defaultValue={sData.number}
                      className="w-full border border-gray-300 rounded-lg p-2.5 bg-gray-100 cursor-not-allowed opacity-70"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">{t("Email", "ઈમેલ")}</label>
                    <input
                      type="text"
                      readOnly
                      defaultValue={sData.email}
                      className="w-full border border-gray-300 rounded-lg p-2.5 bg-gray-100 cursor-not-allowed opacity-70"
                    />
                  </div>
                </div>

                {error[originalIndex] && (
                  <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-center">
                    {error[originalIndex]}
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <Button
                    onClick={() => handleSubUserUpdateProfile(sData, originalIndex)}
                    disabled={loading[originalIndex]}
                    className="gradient-primary gap-1.5 shadow-sm"
                  >
                    <Save size={16} />
                    {loading[originalIndex] ? t("Updating...", "અપડેટ થઈ રહ્યું છે...") : t("Update", "અપડેટ કરો")}
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <div key={sData.id} className="glass-card glass-card-hover rounded-xl p-6 text-center space-y-4 transition-all duration-200 flex flex-col items-center">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center shadow-md overflow-hidden relative group"
                style={{ backgroundColor: avatarBg }}
              >
                {imageUrl ? (
                  <img src={imageUrl} alt={sData.name} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                ) : (
                  <div className="w-10 h-10 text-gray-600">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <h3 className="font-bold text-lg text-gray-900 line-clamp-1">{sData.name || t('Unknown Staff', 'અજાણ્યો સ્ટાફ')}</h3>
                <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                  <Phone size={14} className="text-primary/70" /> 
                  <span>{sData.number || 'N/A'}</span>
                </div>
              </div>

              <div 
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: avatarBg, color: badgeColor }}
              >
                {t("Staff Member", "સ્ટાફ મેમ્બર")}
              </div>

              <div className="flex w-full gap-2 pt-2">
                <Button 
                  onClick={() => setEditingSubUser(prev => ({ ...prev, [originalIndex]: true }))}
                  variant="outline" 
                  size="sm" 
                  className="flex-1 gap-1.5 hover:bg-primary/5 border-primary/20 text-primary"
                >
                  <Edit size={14} /> {t("Update", "અપડેટ")}
                </Button>
                <Button 
                  onClick={() => handleRemove(sData, originalIndex)}
                  variant="outline" 
                  size="sm" 
                  className="flex-1 gap-1.5 border-destructive/20 text-destructive hover:bg-destructive/5 hover:border-destructive/30"
                >
                  <Trash2 size={14} /> {t("Remove", "દૂર કરો")}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recover User */}
      <div className="space-y-4 pt-4">
        <h2 className="text-xl font-bold">{t("Recover User", "યુઝર પાછા મેળવો")}</h2>

        {loadingDeleted ? (
          <p className="text-sm text-muted-foreground">{t("Loading...", "લોડ થઈ રહ્યું છે...")}</p>
        ) : deletedSubUsers.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("No removed staff members.", "કોઈ દૂર કરેલા સ્ટાફ મેમ્બર નથી.")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {deletedSubUsers.map((dData) => (
              <div key={dData._id} className="glass-card rounded-xl p-6 text-center space-y-4 flex flex-col items-center opacity-90">
                <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-md bg-gray-100">
                  <div className="w-10 h-10 text-gray-400">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                </div>

                <div className="space-y-1 w-full">
                  <h3 className="font-bold text-lg text-gray-900 line-clamp-1">{dData.name || t('Unknown Staff', 'અજાણ્યો સ્ટાફ')}</h3>
                  <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                    <Phone size={14} className="text-primary/70" />
                    <span>{dData.number || 'N/A'}</span>
                  </div>
                  {dData.email && (
                    <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                      <Mail size={14} className="text-primary/70 shrink-0" />
                      <span className="truncate">{dData.email}</span>
                    </div>
                  )}
                </div>

                <div className="px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600">
                  {t("Removed", "દૂર કરેલ")}
                  {dData.updatedAt && ` · ${new Date(dData.updatedAt).toLocaleDateString()}`}
                </div>

                <Button
                  onClick={() => handleRecoverSubUser(dData._id)}
                  disabled={recoveringId === dData._id}
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 hover:bg-primary/5 border-primary/20 text-primary"
                >
                  <RotateCcw size={14} />
                  {recoveringId === dData._id ? t("Recovering...", "પાછું મેળવી રહ્યું છે...") : t("Recover", "પાછું મેળવો")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h2 className="text-xl font-bold text-gray-900">{t("Add New Staff Member", "નવો સ્ટાફ મેમ્બર ઉમેરો")}</h2>
              <button
                onClick={() => {
                  const inactiveIdx = subUser.findIndex(u => !u.isActive);
                  if (inactiveIdx !== -1) handleDeleteSubUser(subUser[inactiveIdx], inactiveIdx);
                  setShowAddStaffModal(false);
                }}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {subUser.filter(s => !s.isActive).map((sData) => {
                const originalIndex = subUser.findIndex(u => u.id === sData.id);
                return (
                  <div key={sData.id} className="space-y-4">
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">{t("Name", "નામ")}</label>
                        <input
                          type="text"
                          onChange={(e) => handleSubUserInputChange(originalIndex, 'name', e)}
                          placeholder={t("Enter staff name", "સ્ટાફનું નામ દાખલ કરો")}
                          className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">{t("Contact Number", "સંપર્ક નંબર")}</label>
                        <input
                          type="text"
                          onChange={(e) => handleSubUserInputChange(originalIndex, 'number', e)}
                          placeholder={t("Enter 10-digit number", "10-આંકડાનો નંબર દાખલ કરો")}
                          className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">{t("Email", "ઈમેલ")}</label>
                        <input
                          type="text"
                          onChange={(e) => handleSubUserInputChange(originalIndex, 'email', e)}
                          placeholder={t("Enter email address", "ઈમેલ એડ્રેસ દાખલ કરો")}
                          className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        />
                      </div>
                    </div>

                    {error[originalIndex] && (
                      <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm text-center font-medium">
                        {error[originalIndex]}
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleDeleteSubUser(subUser[originalIndex], originalIndex);
                          setShowAddStaffModal(false);
                        }}
                        className="flex-1 h-12 rounded-xl"
                      >
                        {t("Cancel", "રદ કરો")}
                      </Button>
                      <Button
                        onClick={() => handleSubUserSaveProfile(originalIndex)}
                        disabled={loading[originalIndex]}
                        className="flex-1 h-12 rounded-xl gradient-primary"
                      >
                        {loading[originalIndex] ? t("Saving...", "સાચવી રહ્યું છે...") : t("Save Staff", "સ્ટાફ સાચવો")}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for deletion */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full text-center shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6 scale-110">
              <Trash2 size={30} className="text-red-600" />
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t("Confirm Removal", "દૂર કરવાની પુષ્ટિ કરો")}
            </h3>

            <p className="text-gray-600 mb-8 leading-relaxed">
              {t("Are you sure you want to remove", "શું તમે ખરેખર દૂર કરવા માંગો છો")} <span className="font-bold text-gray-900">{subUserToDelete?.data?.name || 'this staff member'}</span>? 
              {t("All access will be revoked.", "તમામ એક્સેસ રદ કરવામાં આવશે.")}
            </p>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSubUserToDelete(null);
                }}
                className="flex-1 h-12 rounded-xl border-gray-200 font-bold"
              >
                {t("No, Keep", "ના, રાખો")}
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-200"
              >
                {t("Yes, Remove", "હા, દૂર કરો")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
